import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { IHeaderAngularComp } from 'ag-grid-angular/main';

import { CategoryNaturalKey } from '../../documents';
import { EmailNotificationFrequencyOption } from './email-notification-frequency-option';
import { MdlDialogService } from '@angular-mdl/core';
import { GridApi } from 'ag-grid-community/dist/lib/gridApi';
import { EmailPreferenceLevelType } from './levels/email-preference-level-type';
import { IHeaderParams } from 'ag-grid-community';
import { EmailPreferenceReadModel } from './email';
import { EMAIL_PREFERENCE_STORAGE_TOKEN, EmailPreferencesStorage } from './email-preferences-storage';

export interface EmailNotificationHeaderParams extends IHeaderParams {
    userId: number;
    categoryName: string;
    categoryId: CategoryNaturalKey;
    options: EmailNotificationFrequencyOption[];
    currentLevel: EmailPreferenceLevelType;
}

interface Option {
    value: EmailNotificationFrequencyOption;
    text: string;
}

@Component({
    selector: 'email-notification-header',
    template: require('./email-notification-header-ag.component.html'),
    styles: [require('./email-notification-header-ag.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailNotificationHeaderAgComponent implements IHeaderAngularComp {
    public options: Option[];

    private categoryName: string;
    private categoryId: CategoryNaturalKey;
    private gridApi: GridApi;
    private userId: number;
    private currentLevel: EmailPreferenceLevelType;

    constructor(
        @Inject(EMAIL_PREFERENCE_STORAGE_TOKEN)private readonly emailPreferencesStorage: EmailPreferencesStorage,
        private readonly dialogService: MdlDialogService,
    ) { }

    public agInit(params: EmailNotificationHeaderParams): void {
        this.gridApi = params.api;
        this.categoryName = params.categoryName;
        this.categoryId = params.categoryId;
        this.userId = params.userId;
        this.currentLevel = params.currentLevel;

        this.options = params.options.map(option => {
            return {
                value: option,
                text: EmailNotificationFrequencyOption[option]
            };
        });
    }

    public async update(option: Option) {
        const selectedItems = this.gridApi.getSelectedNodes().map(row => row.data) as EmailPreferenceReadModel[];

        if (selectedItems && selectedItems.length > 0 && await this.needToOverride()) {
            const emailPreferencesToSave  = selectedItems.map(item => {
                const preferenceToSave = item.emailPreferences.find(pref => pref.categoryId === this.categoryId);
                if (!preferenceToSave) {
                    throw Error('unable to get preference for provided category');
                }
                
                return preferenceToSave;
            });
            emailPreferencesToSave.forEach(pref => pref.prefValue = option.value);
            this.gridApi.refreshCells();

            await this.emailPreferencesStorage.saveEmailPreferences(emailPreferencesToSave, this.userId);
        }
    }

    private async needToOverride() {
        if (this.currentLevel === EmailPreferenceLevelType.User) {
            return true;
        }

        try {
            const overrideMessage = `Notification option on ${EmailPreferenceLevelType[this.currentLevel]} level overrides the higher level option. Override?`;
            await this.dialogService.confirm(overrideMessage, 'No', 'Yes').toPromise();
            return true;
        } catch {
            return false;
        }
    }
}
